import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  JOB_TYPES, AD_SOURCES, APPLIANCE_TYPES, MANUFACTURERS, STATUSES, formatDate
} from "@/lib/options";
import { CalendarIcon, ChevronLeft, UserPlus, CreditCard, Mail, Link2, Copy, ExternalLink, Check, FileText, Send, XCircle, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useZipAutofill } from "@/hooks/useZipAutofill";
import { PartsPanel } from "@/components/tickets/PartsPanel";
import { PendingPartsBuilder } from "@/components/tickets/PendingPartsBuilder";
import { toast } from "sonner";

const empty = {
  customer_id: "",
  job_type: "repair",
  ad_source: "cash",
  job_description: "",
  problem_notes: "",
  diagnosis: "",
  parts_needed: "",
  dispatch_id: "",
  team: [],
  appliance_type: "refrigerator",
  manufacturer: "ge",
  model_number: "",
  serial_number: "",
  status: "new",
  scheduled_at: null,
  scheduled_time: "09:00",
  // Completion fields (only used when status === "completed")
  total_charged: "",
  parts_cost: "",
  time_estimate_hours: "",
  first_time_complete: false,
  completed_by_tech_id: "",
};

export default function TicketForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const preselectCustomerId = searchParams.get("customer") || "";
  const scrollTo = searchParams.get("scroll") || "";
  const editing = Boolean(id);

  const [form, setForm] = useState({ ...empty, customer_id: preselectCustomerId });
  const [ticketData, setTicketData] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [newTechName, setNewTechName] = useState("");
  const [loading, setLoading] = useState(false);
  const [openNewCustomer, setOpenNewCustomer] = useState(false);
  const [ncDupMatch, setNcDupMatch] = useState(null);
  const [bookedStarts, setBookedStarts] = useState([]);
  const [pendingParts, setPendingParts] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    first_name: "", last_name: "", email: "", phone: "", street: "", city: "", state: "", zip_code: "", county: "",
  });
  const [newCustomerSmsConsent, setNewCustomerSmsConsent] = useState(false);
  const [newCustomerSmsSource, setNewCustomerSmsSource] = useState("verbal");

  // ZIP → city/state/county autofill in the "new customer" mini-form
  useZipAutofill(
    newCustomer.zip_code,
    { city: newCustomer.city, state: newCustomer.state, county: newCustomer.county },
    (patch) => setNewCustomer((s) => ({ ...s, ...patch })),
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadTechnicians = useCallback(async () => {
    try {
      const { data } = await api.get("/technicians");
      setTechnicians(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadTicket = useCallback(async () => {
    if (!editing) return;
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicketData(data);
      setSelectedCustomer(data.customer || null);
      const dt = data.scheduled_at ? new Date(data.scheduled_at) : null;
      setForm({
        customer_id: data.customer_id,
        job_type: data.job_type,
        ad_source: data.ad_source,
        job_description: data.job_description || "",
        problem_notes: data.problem_notes || "",
        diagnosis: data.diagnosis || "",
        parts_needed: data.parts_needed || "",
        dispatch_id: data.dispatch_id || "",
        team: data.team || [],
        appliance_type: data.appliance_type,
        manufacturer: data.manufacturer,
        model_number: data.model_number || "",
        serial_number: data.serial_number || "",
        status: data.status,
        scheduled_at: dt,
        scheduled_time: dt
          ? `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`
          : "09:00",
        total_charged: data.total_charged ?? "",
        parts_cost: data.parts_cost ?? "",
        time_estimate_hours: data.time_estimate_hours ?? "",
        first_time_complete: data.first_time_complete ?? false,
        completed_by_tech_id: data.completed_by_tech_id || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/tickets");
    }
  }, [editing, id, navigate]);

  useEffect(() => {
    loadTechnicians();
    loadTicket();
  }, [loadTechnicians, loadTicket]);

  // When creating a ticket pre-seeded with a customer (?customer=id), fetch that
  // customer so the picker shows their name.
  useEffect(() => {
    if (editing || !preselectCustomerId) return;
    api.get(`/customers/${preselectCustomerId}`)
      .then(({ data }) => setSelectedCustomer(data))
      .catch(() => {});
  }, [editing, preselectCustomerId]);

  // Auto-scroll to payment card when arriving from "Take Payment" button on the detail page
  useEffect(() => {
    if (!editing || scrollTo !== "payment" || loading) return;
    const attempt = () => {
      const el = document.querySelector('[data-testid="payment-card"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const amt = el.querySelector('[data-testid="pay-amount"]');
        if (amt) setTimeout(() => amt.focus(), 400);
        return true;
      }
      return false;
    };
    if (attempt()) return;
    const t1 = setTimeout(attempt, 300);
    const t2 = setTimeout(attempt, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [editing, scrollTo, loading]);

  const combinedDateTime = useMemo(() => {
    if (!form.scheduled_at) return null;
    const [h, m] = (form.scheduled_time || "09:00").split(":").map(Number);
    const d = new Date(form.scheduled_at);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  }, [form.scheduled_at, form.scheduled_time]);

  // Grey out arrival windows already taken on the selected date
  useEffect(() => {
    if (!form.scheduled_at) {
      setBookedStarts([]);
      return;
    }
    const d = new Date(form.scheduled_at);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    let alive = true;
    api.get("/booking/availability", { params: { date: dateStr, ...(editing ? { exclude_ticket: id } : {}) } })
      .then(({ data }) => {
        if (!alive) return;
        // Weekends come back fully "booked" for customers; staff may still schedule them.
        const taken = data.closed ? [] : (data.slots || []).filter((s) => s.booked).map((s) => s.start);
        setBookedStarts(taken);
        const hourMap = { "09:00": 9, "11:00": 11, "13:00": 13, "15:00": 15 };
        const selHour = hourMap[form.scheduled_time];
        if (selHour && taken.includes(selHour)) {
          set("scheduled_time", "");
          toast.info("That arrival window is already booked on this date — pick another");
        }
      })
      .catch(() => setBookedStarts([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scheduled_at, editing, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error("Please choose a customer");
      return;
    }
    if (form.scheduled_at && !form.scheduled_time) {
      toast.error("Please pick an available arrival window");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        job_type: form.job_type,
        ad_source: form.ad_source,
        job_description: form.job_description,
        problem_notes: form.problem_notes,
        diagnosis: form.diagnosis,
        parts_needed: form.parts_needed,
        dispatch_id: form.dispatch_id,
        team: form.team,
        appliance_type: form.appliance_type,
        manufacturer: form.manufacturer,
        model_number: form.model_number,
        serial_number: form.serial_number,
        status: form.status,
        scheduled_at: combinedDateTime ? combinedDateTime.toISOString() : null,
        total_charged: form.status === "completed" && form.total_charged !== "" ? Number(form.total_charged) : null,
        parts_cost: form.status === "completed" && form.parts_cost !== "" ? Number(form.parts_cost) : null,
        time_estimate_hours: form.status === "completed" && form.time_estimate_hours !== "" ? Number(form.time_estimate_hours) : null,
        first_time_complete: form.status === "completed" ? Boolean(form.first_time_complete) : null,
        completed_by_tech_id: form.status === "completed" ? (form.completed_by_tech_id || null) : null,
      };
      if (editing) {
        await api.put(`/tickets/${id}`, payload);
        toast.success("Ticket updated");
        navigate(`/tickets/${id}`);
      } else {
        const { data: created } = await api.post("/tickets", payload);
        for (const p of pendingParts) {
          try {
            await api.post(`/tickets/${created.id}/parts`, { ...p, cost: p.cost === "" ? null : Number(p.cost) });
          } catch (pe) {
            toast.error(`Part "${p.name}" failed to save: ${formatApiError(pe)}`);
          }
        }
        toast.success(pendingParts.length ? `Ticket created with ${pendingParts.length} part(s)` : "Ticket created");
        navigate(`/tickets/${created.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleTech = (id) => {
    setForm((f) => ({
      ...f,
      team: f.team.includes(id) ? f.team.filter((x) => x !== id) : [...f.team, id],
    }));
  };

  const addTechnician = async () => {
    const name = newTechName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/technicians", { name });
      setTechnicians((t) => [...t, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTechName("");
      toast.success("Technician added");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeTechnician = async (id) => {
    try {
      await api.delete(`/technicians/${id}`);
      setTechnicians((t) => t.filter((x) => x.id !== id));
      setForm((f) => ({ ...f, team: f.team.filter((x) => x !== id) }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const createCustomer = async (skipDupCheck = false) => {
    if (!newCustomer.first_name || !newCustomer.last_name || !newCustomer.phone) {
      toast.error("First name, last name and phone are required");
      return;
    }
    try {
      if (!skipDupCheck) {
        const { data: dup } = await api.get("/customers/check-duplicate", {
          params: { phone: newCustomer.phone, email: newCustomer.email || undefined },
        });
        if (dup.match) {
          setNcDupMatch(dup);
          return;
        }
      }
      setNcDupMatch(null);
      const { data } = await api.post("/customers", { ...newCustomer, email: newCustomer.email || null });
      // If the admin ticked the SMS-opt-in checkbox, record consent on the fresh customer.
      if (newCustomerSmsConsent) {
        try {
          await api.post(`/customers/${data.id}/sms-consent`, {
            consented: true,
            source: newCustomerSmsSource,
          });
        } catch (e) {
          toast.error("Customer added, but recording SMS consent failed: " + formatApiError(e));
        }
      }
      toast.success("Customer added");
      setSelectedCustomer(data);
      set("customer_id", data.id);
      setOpenNewCustomer(false);
      setNewCustomer({ first_name: "", last_name: "", email: "", phone: "", street: "", city: "", state: "", zip_code: "", county: "" });
      setNewCustomerSmsConsent(false);
      setNewCustomerSmsSource("verbal");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pickExistingCustomer = (c) => {
    setSelectedCustomer(c);
    set("customer_id", c.id);
    setNcDupMatch(null);
    setOpenNewCustomer(false);
    toast.success(`Using existing customer ${c.first_name} ${c.last_name}`);
  };

  return (
    <div className="space-y-6" data-testid="ticket-form-root">
      <div className="flex items-center gap-3">
        <Link to="/tickets">
          <Button variant="ghost" size="sm" className="gap-1" data-testid="back-to-tickets">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
        </Link>
      </div>
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          {editing ? "Edit Ticket" : "New Ticket"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Fill in customer, appliance, and scheduling details.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Customer */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-slate-900">Customer</h2>
              <Button type="button" size="sm" variant="outline" onClick={() => setOpenNewCustomer(true)} className="gap-1" data-testid="add-customer-btn">
                <UserPlus className="w-4 h-4" /> New customer
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Select customer</Label>
              <CustomerCombobox
                valueId={form.customer_id}
                selected={selectedCustomer}
                onSelect={(c) => { set("customer_id", c.id); setSelectedCustomer(c); }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Appliance */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">Appliance Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Appliance Type</Label>
                <Select value={form.appliance_type} onValueChange={(v) => set("appliance_type", v)}>
                  <SelectTrigger data-testid="select-appliance-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPLIANCE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Manufacturer</Label>
                <Select value={form.manufacturer} onValueChange={(v) => set("manufacturer", v)}>
                  <SelectTrigger data-testid="select-manufacturer"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MANUFACTURERS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Model number</Label>
                <Textarea
                  value={form.model_number}
                  onChange={(e) => set("model_number", e.target.value)}
                  rows={2}
                  data-testid="input-model"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Serial number</Label>
                <Textarea
                  value={form.serial_number}
                  onChange={(e) => set("serial_number", e.target.value)}
                  rows={2}
                  data-testid="input-serial"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job & Schedule */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">Job & Schedule</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Job Type</Label>
                <Select value={form.job_type} onValueChange={(v) => set("job_type", v)}>
                  <SelectTrigger data-testid="select-jobtype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ad Source</Label>
                <Select value={form.ad_source} onValueChange={(v) => set("ad_source", v)}>
                  <SelectTrigger data-testid="select-adsource"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_SOURCES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Appointment date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start font-normal"
                      data-testid="open-calendar"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {form.scheduled_at ? formatDate(form.scheduled_at) : <span className="text-slate-500">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.scheduled_at}
                      onSelect={(d) => set("scheduled_at", d)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Arrival window</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["09:00", 9, "9:00 AM – 11:00 AM"],
                    ["11:00", 11, "11:00 AM – 1:00 PM"],
                    ["13:00", 13, "1:00 PM – 3:00 PM"],
                    ["15:00", 15, "3:00 PM – 5:00 PM"],
                  ].map(([val, hour, label]) => {
                    const taken = bookedStarts.includes(hour);
                    return (
                    <Button
                      key={val}
                      type="button"
                      disabled={taken}
                      variant={form.scheduled_time === val ? "default" : "outline"}
                      className={form.scheduled_time === val ? "bg-blue-700 hover:bg-blue-800" : taken ? "opacity-50 line-through" : ""}
                      onClick={() => set("scheduled_time", val)}
                      data-testid={`window-${val}`}
                    >
                      {label}{taken ? " · Booked" : ""}
                    </Button>
                    );
                  })}
                </div>
                {form.scheduled_time && !["09:00", "11:00", "13:00", "15:00"].includes(form.scheduled_time) && form.scheduled_at && (
                  <p className="text-xs text-amber-700" data-testid="nonstandard-time-note">
                    This ticket currently has a non-standard time ({form.scheduled_time}). Pick a window above to change it.
                  </p>
                )}
              </div>

              <div className="space-y-2 flex items-end">
                {form.scheduled_at && (
                  <Button type="button" variant="ghost" onClick={() => set("scheduled_at", null)} data-testid="clear-date">
                    Clear date
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job description</Label>
              <Textarea
                value={form.job_description}
                onChange={(e) => set("job_description", e.target.value)}
                rows={4}
                placeholder="Describe the issue, parts needed, etc."
                data-testid="input-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Problem notes</Label>
              <Textarea
                value={form.problem_notes}
                onChange={(e) => set("problem_notes", e.target.value)}
                rows={4}
                placeholder="Observations from the call, customer-reported issues, history…"
                data-testid="input-problem-notes"
              />
            </div>
            <div className="space-y-2">
              <Label>Diagnosis</Label>
              <Textarea
                value={form.diagnosis}
                onChange={(e) => set("diagnosis", e.target.value)}
                rows={4}
                placeholder="Technician findings, root cause, recommended repair…"
                data-testid="input-diagnosis"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dispatch ID</Label>
                <Input
                  value={form.dispatch_id}
                  onChange={(e) => set("dispatch_id", e.target.value)}
                  placeholder="e.g. WRN-12345"
                  data-testid="input-dispatch-id"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parts</Label>
              {editing ? <PartsPanel ticketId={id} /> : <PendingPartsBuilder parts={pendingParts} onChange={setPendingParts} />}
            </div>
            <div className="space-y-2">
              <Label>Parts notes (free text, optional)</Label>
              <Textarea
                value={form.parts_needed}
                onChange={(e) => set("parts_needed", e.target.value)}
                rows={2}
                placeholder="Any extra notes about parts…"
                data-testid="input-parts-needed"
              />
            </div>
          </CardContent>
        </Card>

        {/* Team */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">Team</h2>
            <p className="text-xs text-slate-500">Check the technician(s) assigned to this ticket.</p>

            {technicians.length === 0 && (
              <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                No technicians yet. Add one below.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {technicians.map((tech) => {
                const checked = form.team.includes(tech.id);
                return (
                  <label
                    key={tech.id}
                    className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                      checked ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`tech-row-${tech.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-sm text-slate-900">{tech.name}</span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTech(tech.id)}
                        className="w-4 h-4 accent-blue-700 cursor-pointer"
                        data-testid={`tech-check-${tech.id}`}
                      />
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); removeTechnician(tech.id); }}
                      className="text-xs text-slate-400 hover:text-red-600"
                      data-testid={`tech-remove-${tech.id}`}
                      title="Remove technician"
                    >
                      ×
                    </button>
                  </label>
                );
              })}
            </div>

            <div className="flex gap-2 pt-2 border-t border-slate-100">
              <Input
                placeholder="Add technician name…"
                value={newTechName}
                onChange={(e) => setNewTechName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTechnician(); } }}
                data-testid="new-tech-input"
              />
              <Button type="button" variant="outline" onClick={addTechnician} data-testid="add-tech-btn">
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {form.status === "completed" && (
          <Card className="border-emerald-200" data-testid="completion-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-emerald-800">Completion details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total-charged">Total charged ($)</Label>
                <Input
                  id="total-charged"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={form.total_charged}
                  onChange={(e) => set("total_charged", e.target.value)}
                  data-testid="total-charged"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parts-cost">Parts cost ($)</Label>
                <Input
                  id="parts-cost"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={form.parts_cost}
                  onChange={(e) => set("parts_cost", e.target.value)}
                  data-testid="parts-cost"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time-est">Time estimate (hrs)</Label>
                <Input
                  id="time-est"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.25"
                  value={form.time_estimate_hours}
                  onChange={(e) => set("time_estimate_hours", e.target.value)}
                  data-testid="time-estimate"
                />
              </div>
              <div className="space-y-2">
                <Label>Completed by</Label>
                <Select value={form.completed_by_tech_id || "_none"} onValueChange={(v) => set("completed_by_tech_id", v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="select-completed-by"><SelectValue placeholder="Choose tech…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Not set —</SelectItem>
                    {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2 pt-2">
                <input
                  id="ftc"
                  type="checkbox"
                  checked={!!form.first_time_complete}
                  onChange={(e) => set("first_time_complete", e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="first-time-complete"
                />
                <Label htmlFor="ftc" className="cursor-pointer">Completed on first visit</Label>
              </div>
            </CardContent>
          </Card>
        )}

        {editing && user?.role === "admin" && (
          <PaymentCard ticketId={id} form={form} ticket={ticketData} onChanged={loadTicket} />
        )}

        <div className="flex items-center gap-3 justify-end">
          <Link to="/tickets">
            <Button type="button" variant="outline" data-testid="cancel-btn">Cancel</Button>
          </Link>
          <Button type="submit" disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="save-ticket-btn">
            {loading ? "Saving…" : editing ? "Save changes" : "Create ticket"}
          </Button>
        </div>
      </form>

      {/* New customer dialog */}
      <Dialog open={openNewCustomer} onOpenChange={setOpenNewCustomer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New customer</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First name *</Label>
              <Input value={newCustomer.first_name} onChange={(e) => setNewCustomer((s) => ({ ...s, first_name: e.target.value }))} data-testid="nc-first" />
            </div>
            <div className="space-y-2">
              <Label>Last name *</Label>
              <Input value={newCustomer.last_name} onChange={(e) => setNewCustomer((s) => ({ ...s, last_name: e.target.value }))} data-testid="nc-last" />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={newCustomer.phone} onChange={(e) => setNewCustomer((s) => ({ ...s, phone: e.target.value }))} data-testid="nc-phone" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer((s) => ({ ...s, email: e.target.value }))} data-testid="nc-email" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Street</Label>
              <Input value={newCustomer.street} onChange={(e) => setNewCustomer((s) => ({ ...s, street: e.target.value }))} data-testid="nc-street" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={newCustomer.city} onChange={(e) => setNewCustomer((s) => ({ ...s, city: e.target.value }))} data-testid="nc-city" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={newCustomer.state} onChange={(e) => setNewCustomer((s) => ({ ...s, state: e.target.value }))} data-testid="nc-state" />
            </div>
            <div className="space-y-2">
              <Label>Zip code</Label>
              <Input value={newCustomer.zip_code} onChange={(e) => setNewCustomer((s) => ({ ...s, zip_code: e.target.value }))} data-testid="nc-zip" />
            </div>
            <div className="space-y-2">
              <Label>County</Label>
              <Input value={newCustomer.county} onChange={(e) => setNewCustomer((s) => ({ ...s, county: e.target.value }))} data-testid="nc-county" placeholder="Auto-filled from ZIP" />
            </div>
          </div>

          <div
            className={`rounded-md border p-3 mt-4 ${newCustomerSmsConsent ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
            data-testid="nc-sms-consent-block"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={newCustomerSmsConsent}
                onChange={(e) => setNewCustomerSmsConsent(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-400 text-blue-700 focus:ring-blue-600 cursor-pointer"
                data-testid="nc-sms-consent"
              />
              <div className="flex-1 space-y-2">
                <div className="text-sm font-semibold text-slate-800">SMS opt-in on file</div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Check this box <strong>only if the customer has given permission</strong> to
                  send them SMS updates. Required by A2P&nbsp;10DLC.
                </p>
                {newCustomerSmsConsent && (
                  <div className="flex items-center gap-2 pt-1">
                    <Label className="text-xs text-slate-700 shrink-0">How they opted in:</Label>
                    <select
                      value={newCustomerSmsSource}
                      onChange={(e) => setNewCustomerSmsSource(e.target.value)}
                      className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                      data-testid="nc-sms-consent-source"
                    >
                      <option value="verbal">Verbal (on the phone)</option>
                      <option value="written">Written (email or paper)</option>
                      <option value="admin_manual">Admin-recorded</option>
                    </select>
                  </div>
                )}
              </div>
            </label>
          </div>

          {ncDupMatch?.match && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mt-4 space-y-2" data-testid="nc-dup-warning">
              <div className="text-sm font-semibold text-amber-900">
                A customer with this {ncDupMatch.matched_by} already exists
              </div>
              <div className="text-sm text-amber-800">
                {ncDupMatch.match.first_name} {ncDupMatch.match.last_name} · {ncDupMatch.match.phone}
                {ncDupMatch.match.email ? ` · ${ncDupMatch.match.email}` : ""}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={() => pickExistingCustomer(ncDupMatch.match)} className="bg-blue-700 hover:bg-blue-800" data-testid="nc-dup-use-existing">
                  Use existing customer
                </Button>
                <Button size="sm" variant="outline" onClick={() => createCustomer(true)} data-testid="nc-dup-create-anyway">
                  Create anyway
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNewCustomer(false)}>Cancel</Button>
            <Button onClick={() => createCustomer()} className="bg-blue-700 hover:bg-blue-800" data-testid="nc-save">Add customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomerCombobox({ valueId, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = query.trim() ? { search: query.trim() } : {};
        const { data } = await api.get("/customers", { params });
        if (active) setResults(data);
      } catch (e) {
        if (active) toast.error(formatApiError(e));
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(handle); };
  }, [query, open]);

  const label = selected ? `${selected.first_name} ${selected.last_name} · ${selected.phone}` : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="select-customer"
        >
          {label || <span className="text-slate-500">Choose customer…</span>}
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search name or phone…"
            value={query}
            onValueChange={setQuery}
            data-testid="customer-search-input"
          />
          <CommandList>
            {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}
            {!loading && results.length === 0 && <CommandEmpty>No customers found.</CommandEmpty>}
            <CommandGroup>
              {results.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => { onSelect(c); setOpen(false); setQuery(""); }}
                  className="cursor-pointer"
                  data-testid={`customer-option-${c.id}`}
                >
                  <Check className={cn("mr-2 h-4 w-4", valueId === c.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{c.first_name} {c.last_name} · {c.phone}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PaymentCard({ ticketId, form, ticket, onChanged }) {
  const suggested = Number(form.total_charged) > 0 ? Number(form.total_charged).toFixed(2) : "";
  const [amount, setAmount] = useState(suggested);
  const [description, setDescription] = useState(
    `Appliance repair · ${(form.appliance_type || "").replace(/^./, (c) => c.toUpperCase())}`
  );
  const [busy, setBusy] = useState(null); // null | "invoice" | "link" | "estimate" | "publish" | "cancel"
  const [link, setLink] = useState(null); // { type, url, id }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Update the suggested amount if the total charged field changes
    if (Number(form.total_charged) > 0) setAmount(Number(form.total_charged).toFixed(2));
  }, [form.total_charged]);

  const cents = () => Math.round(Number(amount) * 100);

  const hasDraftEstimate =
    !!ticket?.square_estimate_id && (ticket?.square_estimate_status || "DRAFT") === "DRAFT";
  const invoiceStatus = ticket?.square_invoice_status;
  const invoiceUrl = ticket?.square_invoice_public_url;
  const invoiceCents = ticket?.square_invoice_amount_cents;

  const sendInvoice = async () => {
    if (!(cents() > 0)) {
      toast.error("Enter an amount first");
      return;
    }
    setBusy("invoice");
    try {
      const { data } = await api.post(`/tickets/${ticketId}/square/invoice`, {
        amount_cents: cents(),
        description,
      });
      setLink({ type: "invoice", url: data.public_url, id: data.invoice_id });
      toast.success("Invoice sent — customer will get it by email");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const createLink = async () => {
    if (!(cents() > 0)) {
      toast.error("Enter an amount first");
      return;
    }
    setBusy("link");
    try {
      const { data } = await api.post(`/tickets/${ticketId}/square/payment-link`, {
        amount_cents: cents(),
        description,
      });
      setLink({ type: "link", url: data.url, id: data.id });
      toast.success("Payment link ready — share it with the customer");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const saveEstimate = async () => {
    if (!(cents() > 0)) {
      toast.error("Enter an amount first");
      return;
    }
    setBusy("estimate");
    try {
      const { data } = await api.post(`/tickets/${ticketId}/square/estimate`, {
        amount_cents: cents(),
        description,
      });
      setLink({ type: "estimate", url: data.public_url, id: data.invoice_id });
      toast.success("Estimate saved as draft — approve & send when you're ready");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const publishEstimate = async () => {
    setBusy("publish");
    try {
      const { data } = await api.post(`/tickets/${ticketId}/square/estimate/publish`);
      toast.success("Estimate approved & sent — customer will get an email");
      setLink({ type: "invoice", url: data.public_url, id: ticket?.square_estimate_id });
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const cancelEstimate = async () => {
    if (!window.confirm("Cancel this draft estimate? This can't be undone.")) return;
    setBusy("cancel");
    try {
      await api.post(`/tickets/${ticketId}/square/estimate/cancel`);
      toast.success("Estimate cancelled");
      setLink(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const fmtMoney = (c) => (typeof c === "number" ? `$${(c / 100).toFixed(2)}` : "—");

  return (
    <Card className="border-blue-200" data-testid="payment-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg text-blue-800 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Take payment (Square)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pay-amount">Amount ($)</Label>
            <Input
              id="pay-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 180.00"
              data-testid="pay-amount"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="pay-desc">Description (what you did — customer sees this on the invoice)</Label>
            <Textarea
              id="pay-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Refrigerator repair – replaced ice maker valve and cleared drain line."
              className="min-h-[90px]"
              maxLength={1000}
              data-testid="pay-desc"
            />
            <div className="text-xs text-slate-400 text-right">{description.length}/1000</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            type="button"
            onClick={saveEstimate}
            disabled={busy !== null || hasDraftEstimate}
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="save-estimate-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            {busy === "estimate" ? "Saving…" : hasDraftEstimate ? "Estimate saved" : "Save as Estimate"}
          </Button>
          <Button
            type="button"
            onClick={sendInvoice}
            disabled={busy !== null}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="send-invoice-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            {busy === "invoice" ? "Sending…" : "Send Invoice (Bill Later)"}
          </Button>
          <Button
            type="button"
            onClick={createLink}
            disabled={busy !== null}
            variant="outline"
            className="border-blue-300 text-blue-800 hover:bg-blue-50"
            data-testid="create-link-btn"
          >
            <Link2 className="w-4 h-4 mr-2" />
            {busy === "link" ? "Creating…" : "Create Pay Link (Pay Now)"}
          </Button>
        </div>

        {hasDraftEstimate && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-3"
            data-testid="draft-estimate-panel"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Draft estimate · {fmtMoney(ticket?.square_estimate_amount_cents)}
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-amber-200 text-amber-900 px-2 py-0.5 rounded">
                {ticket?.square_estimate_status || "DRAFT"}
              </span>
            </div>
            {ticket?.square_estimate_description && (
              <p className="text-xs text-amber-900/80 whitespace-pre-wrap">
                {ticket.square_estimate_description}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                onClick={publishEstimate}
                disabled={busy !== null}
                className="bg-amber-600 hover:bg-amber-700 flex-1"
                data-testid="publish-estimate-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {busy === "publish" ? "Sending…" : "Approve & Send to Customer"}
              </Button>
              <Button
                type="button"
                onClick={cancelEstimate}
                disabled={busy !== null}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="cancel-estimate-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {busy === "cancel" ? "Cancelling…" : "Cancel"}
              </Button>
            </div>
            <p className="text-[11px] text-amber-800/80">
              Estimates aren't sent to the customer until you approve & send. That converts it into
              a live invoice they can pay online.
            </p>
          </div>
        )}

        {!hasDraftEstimate && invoiceUrl && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2"
            data-testid="current-invoice-panel"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">
                Current invoice · {fmtMoney(invoiceCents)}
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded">
                {invoiceStatus || "SENT"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate">
                {invoiceUrl}
              </code>
              <a href={invoiceUrl} target="_blank" rel="noreferrer">
                <Button type="button" size="sm" variant="outline">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </a>
            </div>
          </div>
        )}

        {link && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="payment-link-result">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
              {link.type === "invoice" ? "Invoice URL" : link.type === "estimate" ? "Estimate URL (internal)" : "Payment Link"}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate" data-testid="link-url">
                {link.url}
              </code>
              <Button type="button" size="sm" variant="outline" onClick={copyLink} data-testid="copy-link-btn">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
              <a href={link.url} target="_blank" rel="noreferrer">
                <Button type="button" size="sm" variant="outline">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </a>
            </div>
            <p className="text-xs text-slate-600">
              {link.type === "invoice"
                ? "Square emailed this to the customer. You'll be notified here when they pay."
                : link.type === "estimate"
                ? "Draft only — the customer hasn't received anything yet. Preview then Approve & Send when ready."
                : "Share this URL with the customer (text, email, or show the QR code). Works on any device."}
            </p>
          </div>
        )}

        <p className="text-xs text-slate-500">
          Sandbox mode — no real charges. Test card: <code>4111 1111 1111 1111</code> · any future date · any CVV.
        </p>
      </CardContent>
    </Card>
  );
}
